import { ComponentHeadingSource, getComponentHeadingSource } from '@wix/a11y-adapters';
import { HeadingStructureFailedReason } from '@wix/a11y-rules';
import { Box, Text, TextButton, Timeline } from '@wix/design-system';
import { Trans, useTranslation } from '@wix/fe-essentials/react';
import { ElementStatus } from '@wix/rules-panel-ui';
import { StatusAlertFilledSmall, StatusCompleteFilledSmall } from '@wix/wix-ui-icons-common';
import React from 'react';
import DEFAULT_HTML_TAG_VIDEO from '../../../../../assets/MP4/HTML_Tag.mp4';
import DEFAULT_SEO_A11Y_VIDEO from '../../../../../assets/MP4/SEO_a11y.mp4';
import { generalToolTips } from '../../../../../bi/types';
import { useBiLogger } from '../../../../../hooks/use-bi-logger';
import { useGenericElement } from '../../../../../hooks/use-generic-element';
import { useSessionInfoContext } from '../../../../../hooks/use-session-info-context';
import { HTML_TAG_VIDEO_URL_MAP, SEO_A11Y_VIDEO_URL_MAP } from './constants';
import { HeadingStructureRuleGenericContentPopover } from './heading-structure-rule-generic-content-popover';
export const HeadingStructureRuleGenericTimeline = _ref => {
  let {
    index,
    status,
    failedReason,
    pageId,
    id
  } = _ref;
  const {
    handleRescanClick
  } = useGenericElement({
    getComponentStyleSource: getComponentHeadingSource,
    pageId,
    unknownValue: ComponentHeadingSource.UNKNOWN
  });
  const biLogger = useBiLogger();
  const {
    t
  } = useTranslation();
  const isFailedReasonMultipleMainHeadingsOrNoMainHeading = failedReason === HeadingStructureFailedReason.NoMainHeading || failedReason === HeadingStructureFailedReason.MultipleMainHeadings;
  const {
    locale
  } = useSessionInfoContext();
  const HTML_TAG_VIDEO = HTML_TAG_VIDEO_URL_MAP[locale == null ? void 0 : locale.toUpperCase()] || DEFAULT_HTML_TAG_VIDEO;
  const SEO_A11Y_VIDEO = SEO_A11Y_VIDEO_URL_MAP[locale == null ? void 0 : locale.toUpperCase()] || DEFAULT_SEO_A11Y_VIDEO;
  const changesExplanationContent = /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Box, {
    paddingBottom: "SP1"
  }, /*#__PURE__*/React.createElement(Text, {
    size: "tiny"
  }, /*#__PURE__*/React.createElement(Trans, {
    components: {
      boldText: /*#__PURE__*/React.createElement(Text, {
        size: "tiny",
        weight: "bold"
      })
    },
    i18nKey: "accessibility-wizard.rules-list.heading-definition.timeline.first-step.first-part"
  }))), /*#__PURE__*/React.createElement(Text, {
    size: "tiny"
  }, /*#__PURE__*/React.createElement(Trans, {
    components: {
      boldText: /*#__PURE__*/React.createElement(Text, {
        size: "tiny",
        weight: "bold"
      }),
      textButtonForHTML: /*#__PURE__*/React.createElement(HeadingStructureRuleGenericContentPopover, {
        index: index,
        isSeo: false,
        status: status,
        videoPath: HTML_TAG_VIDEO
      }),
      textButtonForSEO: /*#__PURE__*/React.createElement(HeadingStructureRuleGenericContentPopover, {
        index: index,
        status: status,
        videoPath: SEO_A11Y_VIDEO,
        isSeo: true
      })
    },
    i18nKey: "accessibility-wizard.rules-list.heading-definition.timeline.first-step.second-part"
  })));
  const failureReasonContent = /*#__PURE__*/React.createElement(Text, {
    size: "tiny"
  }, /*#__PURE__*/React.createElement(Trans, {
    components: {
      boldText: /*#__PURE__*/React.createElement(Text, {
        size: "tiny",
        weight: "bold"
      })
    },
    i18nKey: `accessibility-wizard.rules-list.heading-definition.timeline.second-step.${failedReason}`
  }));
  const firstStep = isFailedReasonMultipleMainHeadingsOrNoMainHeading ? failureReasonContent : changesExplanationContent;
  const secondStep = isFailedReasonMultipleMainHeadingsOrNoMainHeading ? changesExplanationContent : failureReasonContent;
  const thirdStep = /*#__PURE__*/React.createElement(Text, {
    size: "tiny"
  }, /*#__PURE__*/React.createElement(Trans, {
    components: {
      rescanButton: /*#__PURE__*/React.createElement(TextButton, {
        size: "tiny",
        skin: "dark",
        underline: "always",
        weight: "bold",
        onClick: () => {
          handleRescanClick(id);
          biLogger.taskElementCtaClick({
            ctaName: generalToolTips.rescanPage,
            index,
            status: status === ElementStatus.Passed ? ElementStatus.ManualPassed : status === ElementStatus.Failed ? ElementStatus.ManualFailed : status
          });
        }
      })
    },
    i18nKey: "accessibility-wizard.rules-list.heading-definition.timeline.third-step"
  }));
  const failedItems = [{
    label: firstStep
  }, {
    label: secondStep
  }, {
    label: thirdStep
  }];
  const passedItems = [{
    label: changesExplanationContent
  }, {
    label: thirdStep
  }];
  return /*#__PURE__*/React.createElement(React.Fragment, null, status === ElementStatus.Failed ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Timeline, {
    items: [{
      customPrefix: /*#__PURE__*/React.createElement(Box, {
        color: "R10"
      }, /*#__PURE__*/React.createElement(StatusAlertFilledSmall, {
        size: "18px"
      })),
      label: /*#__PURE__*/React.createElement(Text, {
        size: "tiny",
        skin: "error",
        weight: "thin"
      }, t(`accessibility-wizard.rules-list.heading-definition.errors.generic.${failedReason}`))
    }]
  }), /*#__PURE__*/React.createElement(Timeline, {
    gap: "20px",
    items: failedItems,
    skin: "dark"
  })) : /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Timeline, {
    items: [{
      customPrefix: /*#__PURE__*/React.createElement(Box, {
        color: "G10"
      }, /*#__PURE__*/React.createElement(StatusCompleteFilledSmall, {
        size: "18px"
      })),
      label: /*#__PURE__*/React.createElement(Text, {
        size: "tiny",
        weight: "thin"
      }, t(`accessibility-wizard.rules-list.heading-definition.success.generic`))
    }]
  }), /*#__PURE__*/React.createElement(Timeline, {
    gap: "20px",
    items: passedItems,
    skin: "dark"
  })));
};
//# sourceMappingURL=heading-structure-rule-generic-timeline.js.map